import classNames from 'classnames';
import DOMPurify from 'dompurify';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import styles from './styles/styles.module.scss';

const messages = defineMessages({
  SHOW_MORE: { defaultMessage: 'Show more' },
  SHOW_LESS: { defaultMessage: 'Show less' },
  MORE: { defaultMessage: 'more' },
  LESS: { defaultMessage: 'less' }
});

type ShowMoreTextProps = {
  text?: string;
  innerHtml?: string;
  textComponent?: React.ReactNode;
  maxLines: number;
  preventExpansion?: boolean;
  className?: string;
  showMoreBackgroundColor?: string;
  testIdPrefix?: string;
  hideShowLess?: boolean;
  wrapLinkToNewLine?: boolean;
  useShortText?: boolean;
  // Skipping sanitization should only be done if the content has already been sanitized on the server
  // This may be necessary for content to match during SSR and hydration
  skipHtmlSanitization?: boolean;
  onClick?: () => void;
  moreHasCustomAction?: boolean;
  expandOnLoad?: boolean;
};

const ShowMoreText = ({
  text,
  innerHtml,
  textComponent,
  maxLines,
  className,
  preventExpansion = false,
  showMoreBackgroundColor,
  testIdPrefix,
  hideShowLess = false,
  wrapLinkToNewLine = false,
  useShortText = false,
  skipHtmlSanitization = false,
  onClick,
  moreHasCustomAction = false,
  expandOnLoad = false
}: ShowMoreTextProps) => {
  const hasText = text && text.length > 0;
  const {
    formattedDefaultMessages: { SHOW_LESS, SHOW_MORE, MORE, LESS }
  } = useFormatMessage(messages);
  const [isExpanded, setIsExpanded] = useState(expandOnLoad);
  const [isTruncated, setIsTruncated] = useState(false);
  const textContainer = useRef<HTMLDivElement>(null);

  const handleResize = useCallback(() => {
    if (!isExpanded && textContainer.current) {
      setIsTruncated(textContainer.current.scrollHeight > textContainer.current.clientHeight);
    }
  }, [isExpanded, textContainer]);

  useEffect(() => {
    if (hasText) {
      handleResize();
      window.addEventListener('resize', debounce(handleResize, 300));
    }

    return () => {
      if (hasText) {
        window.removeEventListener('resize', debounce(handleResize, 300));
      }
    };
  }, [handleResize]);

  const onShowClick = () => {
    onClick?.();
    setIsExpanded(!isExpanded);
  };

  const showMoreElement = useMemo(() => {
    if (isExpanded && hideShowLess) {
      return null;
    }
    const createTextElement = (showText: string) =>
      preventExpansion ? (
        <span {...(testIdPrefix ? { 'data-testid': `${testIdPrefix}_ShowMoreLabel` } : {})}>{showText}</span>
      ) : (
        <button
          onClick={moreHasCustomAction ? onClick : onShowClick}
          type="button"
          {...(testIdPrefix ? { 'data-testid': `${testIdPrefix}_ShowMoreButton` } : {})}
        >
          {showText}
        </button>
      );

    if (isExpanded) {
      return createTextElement(useShortText ? LESS : SHOW_LESS);
    }

    return isTruncated ? (
      <>
        {!wrapLinkToNewLine && '... '}
        {createTextElement(useShortText ? MORE : SHOW_MORE)}
      </>
    ) : null;
  }, [SHOW_LESS, SHOW_MORE, MORE, LESS, isExpanded, wrapLinkToNewLine, useShortText, isTruncated, preventExpansion]);

  const showMoreAtEndOfText = !showMoreElement && moreHasCustomAction;

  const commentElement = useMemo(() => {
    const sharedAttributes = {
      className: styles.text,
      style: { WebkitLineClamp: isExpanded ? undefined : maxLines },
      ref: textContainer,
      ...(testIdPrefix ? { 'data-testid': `${testIdPrefix}_ShowMoreText` } : {})
    };

    if (innerHtml && skipHtmlSanitization && !textComponent) {
      return <div {...sharedAttributes} dangerouslySetInnerHTML={{ __html: innerHtml }} />;
    }

    if (innerHtml && typeof window !== 'undefined' && !textComponent) {
      const sanitizeConfig = {
        ALLOWED_TAGS: ['strong', 'a'] // only supports bolding and links
      };
      const sanitizedHTML = DOMPurify.sanitize(innerHtml, sanitizeConfig);
      return <div {...sharedAttributes} dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />;
    }

    return (
      <div {...sharedAttributes}>
        {textComponent || text}
        {showMoreAtEndOfText && (
          <div className={classNames(styles.showMoreButton, styles.moreInlineButton)}>
            <button onClick={onClick} type="button">
              {useShortText ? MORE : SHOW_MORE}
            </button>
          </div>
        )}
      </div>
    );
  }, [text, innerHtml, isExpanded, maxLines, showMoreAtEndOfText, onClick, useShortText]);

  if (!hasText && !textComponent) {
    return null;
  }

  return (
    <div className={classNames(styles.container, className)}>
      {commentElement}
      {showMoreElement && (
        <div
          className={classNames(styles.showMore, styles.showMoreButton, {
            [styles.showMoreNewLine]: wrapLinkToNewLine,
            [styles.showLess]: isExpanded
          })}
          // Override the SCSS gradient (which fades to --color-background-primary) so the fade matches the caller's container.
          style={
            showMoreBackgroundColor
              ? { background: `linear-gradient(90deg, var(--color-neutral-transparent) 0%, ${showMoreBackgroundColor} 36.11%)` }
              : undefined
          }
        >
          {showMoreElement}
        </div>
      )}
    </div>
  );
};

export default ShowMoreText;
